import React, { useEffect, useState } from 'react'
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import { Visibility } from '@material-ui/icons'
import { CommissionSettlement } from '../types'
import { money } from '../utils'
import SettlementDetail from './SettlementDetail'
import SettlementDeleteDialog from './SettlementDeleteDialog'

const useStyles = makeStyles(theme => ({
  history: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderTop: '1px solid #dde1e5'
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#2a2f36',
    marginBottom: theme.spacing(1.5)
  },
  tableContainer: {
    border: '1px solid #e1e4e8',
    borderRadius: 6,
    boxShadow: '0 2px 8px rgba(31, 38, 46, 0.05)',
    '& .MuiTableCell-head': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      background: '#f56600',
      color: '#fff',
      fontWeight: 700,
      borderBottom: '1px solid #d95b00'
    },
    '& .MuiTableRow-root': { transition: 'background-color 120ms ease' },
    '& .MuiTableRow-root:hover': { backgroundColor: '#fafbfc' },
    '& .MuiTableCell-body': {
      borderBottomColor: '#eceef1',
      paddingTop: 9,
      paddingBottom: 9
    }
  }
}))

interface Props {
  settlements: CommissionSettlement[]
  canDelete: boolean
  mutating: boolean
  lastDeletedSettlementId?: string
  onDelete: (settlementId: string, reason: string) => void
}

const SettlementHistory: React.FC<Props> = ({
  settlements,
  canDelete,
  mutating,
  lastDeletedSettlementId,
  onDelete
}) => {
  const classes = useStyles()
  const [detail, setDetail] = useState<CommissionSettlement>()
  const [deleteTarget, setDeleteTarget] = useState<CommissionSettlement>()

  useEffect(() => {
    if (!lastDeletedSettlementId || lastDeletedSettlementId !== deleteTarget?._id) return
    setDeleteTarget(undefined)
    setDetail(undefined)
  }, [deleteTarget, lastDeletedSettlementId])

  return (
    <Box className={classes.history}>
      <Typography className={classes.sectionTitle} variant="h6" gutterBottom>
        Comisiones del período
      </Typography>
      <TableContainer className={classes.tableContainer} component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha de pago</TableCell>
              <TableCell>Bono meta</TableCell>
              <TableCell>Conceptos</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Detalle</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settlements.map(settlement => (
              <TableRow key={settlement._id}>
                <TableCell>
                  {settlement.paidAt ? new Date(settlement.paidAt).toLocaleDateString('es-CO') : '-'}
                </TableCell>
                <TableCell>{settlement.goalBonusIncluded ? money(settlement.goalBonus) : '-'}</TableCell>
                <TableCell>{settlement.lineItems.length}</TableCell>
                <TableCell align="right">{money(settlement.totalAmount)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver detalle">
                    <IconButton aria-label="Ver detalle" onClick={() => setDetail(settlement)}>
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!settlements.length ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay comisiones pagadas este período.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      <SettlementDetail
        settlement={detail}
        canDelete={canDelete}
        mutating={mutating}
        onClose={() => setDetail(undefined)}
        onDelete={() => setDeleteTarget(detail)}
      />
      <SettlementDeleteDialog
        settlement={deleteTarget}
        mutating={mutating}
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(undefined)}
        onConfirm={reason => {
          if (deleteTarget) onDelete(deleteTarget._id, reason)
        }}
      />
    </Box>
  )
}

export default SettlementHistory
